import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Edit2,
  Save,
  X,
  Trash2,
  Calendar,
  User,
  Clock,
  Eye,
  Plus,
  Loader2,
  Sparkles,
  MoreVertical,
  Search,
  Filter,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";
import { toast } from "react-toastify";

// Delete Modal Component
const DeleteModal = ({ isOpen, onClose, onConfirm, blogTitle, isDeleting }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
          <Trash2 className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800 text-center mb-2">
          Delete Blog Post?
        </h3>
        <p className="text-gray-600 text-center mb-6">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-gray-800">"{blogTitle}"</span>?
          This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-5 h-5" />
                Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// Blog Card Component
const BlogCard = ({
  blog,
  onEdit,
  onDelete,
  isEmployee,
  onView,
  isHighlighted,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();

  const handleTitleClick = () => {
    // Create a URL-friendly slug from the title
    const slug = blog.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

    navigate(`/blog/${slug}`, { state: { blogId: blog._id } });

    // Scroll to the card after navigation
    setTimeout(() => {
      const element = document.getElementById(`blog-${blog._id}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  return (
    <article
      id={`blog-${blog._id}`}
      className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group ${
        isHighlighted
          ? "border-4 border-blue-500 ring-4 ring-blue-200 scale-105"
          : "border border-gray-100"
      }`}
    >
      {/* Blog Header */}
      <div className="relative bg-gradient-to-r from-blue-500 to-purple-600 p-6">
        <div className="flex items-center justify-between">
          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-sm font-semibold text-gray-800 rounded-full">
            {blog.category}
          </span>
          {isEmployee && (
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
              >
                <MoreVertical className="w-5 h-5 text-gray-700" />
              </button>
              {showMenu && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-10">
                  <button
                    onClick={() => {
                      onEdit(blog);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-blue-50 flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      onDelete(blog);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Blog Content */}
      <div className="p-6">
        {/* Author & Date */}
        <div className="flex items-center gap-4 mb-4 text-sm text-gray-600 flex-wrap">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{blog.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>{new Date(blog.date).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{blog.readTime}</span>
          </div>
        </div>

        {/* Title - Clickable */}
        <h2
          onClick={handleTitleClick}
          className="text-2xl font-bold text-gray-800 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors cursor-pointer"
        >
          {blog.title}
        </h2>

        {/* Excerpt */}
        <p className="text-gray-600 mb-4 line-clamp-3">
          {blog.content.substring(0, 150)}...
        </p>

        {/* Stats & Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Eye className="w-4 h-4" />
            <span>{blog.views} views</span>
          </div>
          <button
            onClick={() => onView(blog)}
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
          >
            Read More
          </button>
        </div>
      </div>
    </article>
  );
};

// Blog View Modal
const BlogViewModal = ({ isOpen, onClose, blog }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen || !blog) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in overflow-y-auto">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 animate-scale-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <span className="px-4 py-2 bg-white/90 backdrop-blur-sm text-sm font-semibold text-gray-800 rounded-full">
              {blog.category}
            </span>
            <button
              onClick={onClose}
              className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 max-h-[70vh] overflow-y-auto">
          {/* Title */}
          <h1 className="text-4xl font-black text-gray-800 mb-6">
            {blog.title}
          </h1>

          {/* Author Info & Meta */}
          <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                {blog.author.charAt(0)}
              </div>
              <div>
                <p className="font-semibold text-gray-800">{blog.author}</p>
                <p className="text-sm text-gray-500">{blog.authorRole}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(blog.date).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{blog.readTime}</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                <span>{blog.views} views</span>
              </div>
            </div>
          </div>

          {/* Blog Content */}
          <div className="prose prose-lg max-w-none">
            {blog.content.split("\n\n").map((paragraph, index) => {
              if (paragraph.startsWith("**") && paragraph.endsWith("**")) {
                return (
                  <h3
                    key={index}
                    className="text-2xl font-bold text-gray-800 mt-8 mb-4"
                  >
                    {paragraph.replace(/\*\*/g, "")}
                  </h3>
                );
              }
              return (
                <p key={index} className="text-gray-700 leading-relaxed mb-4">
                  {paragraph}
                </p>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// Blog Editor Modal
const BlogEditorModal = ({ isOpen, onClose, blog, onSave, isCreating }) => {
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || "",
        content: blog.content || "",
        category: blog.category || "",
      });
    } else {
      setFormData({
        title: "",
        content: "",
        category: "",
      });
    }
  }, [blog, isOpen]);

  const handleSubmit = async () => {
    if (!formData.title || !formData.content || !formData.category) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await onSave(blog?._id, formData);
      onClose();
      toast.success(
        isCreating
          ? "Blog created successfully! 🎉"
          : "Blog updated successfully! 🎉",
      );
    } catch (error) {
      console.error("Save failed:", error);
      toast.error(error.response?.data?.message || "Failed to save blog");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in overflow-y-auto">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 animate-scale-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              {isCreating ? "Create New Blog Post" : "Edit Blog Post"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Blog Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                placeholder="Enter an engaging title..."
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                className="w-full h-max px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
              >
                <option value="">Select a category</option>
                <option value="Technology">Technology</option>
                <option value="Development">Development</option>
                <option value="Design">Design</option>
                <option value="Business">Business</option>
                <option value="Marketing">Marketing</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Content *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                rows={12}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors resize-none"
                placeholder="Write your blog content here... Use **text** for headings."
              />
              <p className="text-sm text-gray-500 mt-2">
                Tip: Use **Text** to create bold headings in your content
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 rounded-b-2xl flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isCreating ? "Create Post" : "Update Post"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// Main Blog Component
const BlogComponent = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showFilters, setShowFilters] = useState(false);
  const [highlightedBlogId, setHighlightedBlogId] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { userData: user, token } = useSelector((state) => state.userSlice);
  const userType = user?.user_type || "";
  const isUserEmployee =
    userType === "employee" || userType === "manager" || userType === "admin";

  const categories = [
    "All",
    "Technology",
    "Development",
    "Design",
    "Business",
    "Marketing",
    "Other",
  ];

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    let filtered = [...blogs];

    if (selectedCategory !== "All") {
      filtered = filtered.filter((blog) => blog.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (blog) =>
          blog.title.toLowerCase().includes(query) ||
          blog.content.toLowerCase().includes(query) ||
          blog.author.toLowerCase().includes(query),
      );
    }

    setFilteredBlogs(filtered);
  }, [blogs, searchQuery, selectedCategory]);

  // Handle highlighting from navigation (both state and URL params)
  useEffect(() => {
    // Get slug from URL
    const pathParts = location.pathname.split("/");
    const slug = pathParts[pathParts.length - 1];

    // If there's a slug and it's not just "blog"
    if (slug && slug !== "blog" && blogs.length > 0) {
      // Try to find blog by state first
      if (location.state?.blogId) {
        setHighlightedBlogId(location.state.blogId);
      } else {
        // Find blog by matching slug with title
        const matchedBlog = blogs.find((blog) => {
          const blogSlug = blog.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
          return blogSlug === slug;
        });

        if (matchedBlog) {
          setHighlightedBlogId(matchedBlog._id);

          // Scroll to the blog card
          setTimeout(() => {
            const element = document.getElementById(`blog-${matchedBlog._id}`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }, 300);
        }
      }

      // Remove highlight and clean URL after 3 seconds
      setTimeout(() => {
        setHighlightedBlogId(null);
        navigate("/blog", { replace: true, state: {} });
      }, 3000);
    }
  }, [location.pathname, location.state, blogs, navigate]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/blog/fetch`);

      if (response.data.success) {
        const mappedBlogs = response.data.data.map((blog) => ({
          _id: blog._id,
          title: blog.title,
          content: blog.content,
          category: blog.category,
          author: blog.author,
          authorRole: blog.authorRole,
          date: blog.createdAt,
          readTime: blog.readTime,
          views: blog.views,
        }));
        setBlogs(mappedBlogs);
      } else {
        toast.error("Failed to fetch blogs");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error(error.response?.data?.message || "Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlog = () => {
    setSelectedBlog(null);
    setIsCreating(true);
    setShowEditorModal(true);
  };

  const handleEditBlog = (blog) => {
    setSelectedBlog(blog);
    setIsCreating(false);
    setShowEditorModal(true);
  };

  const handleDeleteBlog = (blog) => {
    setBlogToDelete(blog);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      const response = await axios.delete(
        `${apiUrl}/blog/delete/${blogToDelete._id}`,
        {
          headers: {
            token: token,
          },
        },
      );

      if (response.data.success) {
        setBlogs(blogs.filter((blog) => blog._id !== blogToDelete._id));
        setShowDeleteModal(false);
        toast.success("Blog deleted successfully!");
      } else {
        toast.error(response.data.message || "Failed to delete blog");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Failed to delete blog");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBlog = async (id, formData) => {
    try {
      if (isCreating) {
        const response = await axios.post(
          `${apiUrl}/blog/create`,
          {
            ...formData,
            author: user?.name || "Anonymous",
            authorRole: userType || "Employee",
          },
          {
            headers: {
              token: token,
            },
          },
        );

        if (response.data.success) {
          const newBlog = {
            _id: response.data.data._id,
            title: response.data.data.title,
            content: response.data.data.content,
            category: response.data.data.category,
            author: response.data.data.author,
            authorRole: response.data.data.authorRole,
            date: response.data.data.createdAt,
            readTime: response.data.data.readTime,
            views: response.data.data.views,
          };
          setBlogs([newBlog, ...blogs]);
        } else {
          throw new Error(response.data.message || "Failed to create blog");
        }
      } else {
        const response = await axios.put(
          `${apiUrl}/blog/update/${id}`,
          formData,
          {
            headers: {
              token: token,
            },
          },
        );

        if (response.data.success) {
          setBlogs(
            blogs.map((blog) =>
              blog._id === id
                ? {
                    ...blog,
                    title: response.data.data.title,
                    content: response.data.data.content,
                    category: response.data.data.category,
                  }
                : blog,
            ),
          );
        } else {
          throw new Error(response.data.message || "Failed to update blog");
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const handleViewBlog = async (blog) => {
    try {
      const response = await axios.get(`${apiUrl}/blog/${blog._id}`);
      if (response.data.success) {
        setBlogs(
          blogs.map((b) =>
            b._id === blog._id ? { ...b, views: response.data.data.views } : b,
          ),
        );
        setSelectedBlog({ ...blog, views: response.data.data.views });
      } else {
        setSelectedBlog(blog);
      }
      setShowViewModal(true);
    } catch (error) {
      console.error("Error viewing blog:", error);
      setSelectedBlog(blog);
      setShowViewModal(true);
    }
  };

  const handleRefresh = () => {
    setSearchQuery("");
    setSelectedCategory("All");
    fetchBlogs();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 p-6 ">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 blur-3xl"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h1 className="text-5xl h-max font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Funingo Blogs
                  </h1>
                </div>
                <p className="text-gray-600 text-lg ml-16">
                  {isUserEmployee
                    ? "Create, manage, and share your thoughts with the world"
                    : "Explore insights, stories, and knowledge from our team"}
                </p>
              </div>
              {isUserEmployee && (
                <button
                  onClick={handleCreateBlog}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create Blog
                </button>
              )}
            </div>

            {/* Search and Filter Bar */}
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search Bar */}
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search blogs by title, content, or author..."
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                  />
                </div>

                {/* Filter Button (Mobile) */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="md:hidden px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Filter className="w-5 h-5" />
                  Filters
                </button>

                {/* Category Filter (Desktop) */}
                <div className="hidden md:flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-500" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 h-max py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors bg-white"
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Refresh
                </button>
              </div>

              {/* Category Filter (Mobile) */}
              {showFilters && (
                <div className="md:hidden mt-4 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Filter by Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors bg-white"
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Results Count */}
            {(searchQuery || selectedCategory !== "All") && (
              <div className="mb-4 text-gray-600">
                Found{" "}
                <span className="font-semibold text-gray-800">
                  {filteredBlogs.length}
                </span>{" "}
                blog{filteredBlogs.length !== 1 ? "s" : ""}
                {searchQuery && ` matching "${searchQuery}"`}
                {selectedCategory !== "All" && ` in ${selectedCategory}`}
              </div>
            )}
          </div>
        </div>

        {/* Blog Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            <div className="col-span-full flex items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
              <span className="ml-3 text-gray-600 text-lg">
                Loading blogs...
              </span>
            </div>
          ) : filteredBlogs.length > 0 ? (
            filteredBlogs.map((blog) => (
              <BlogCard
                key={blog._id}
                blog={blog}
                onEdit={handleEditBlog}
                onDelete={handleDeleteBlog}
                onView={handleViewBlog}
                isEmployee={isUserEmployee}
                isHighlighted={highlightedBlogId === blog._id}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-20">
              <div className="mb-4">
                <Sparkles className="w-16 h-16 text-gray-400 mx-auto" />
              </div>
              {searchQuery || selectedCategory !== "All" ? (
                <>
                  <p className="text-gray-500 text-xl">No blogs found</p>
                  <p className="text-gray-400 mt-2">
                    Try adjusting your search or filters
                  </p>
                  <button
                    onClick={handleRefresh}
                    className="mt-4 px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors"
                  >
                    Clear Filters
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-500 text-xl">No blog posts yet</p>
                  <p className="text-gray-400 mt-2">
                    {isUserEmployee
                      ? "Create your first blog post to get started!"
                      : "Check back soon for new content!"}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <BlogViewModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        blog={selectedBlog}
      />

      <BlogEditorModal
        isOpen={showEditorModal}
        onClose={() => setShowEditorModal(false)}
        blog={selectedBlog}
        onSave={handleSaveBlog}
        isCreating={isCreating}
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        blogTitle={blogToDelete?.title}
        isDeleting={loading}
      />

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default BlogComponent;
